import { useCallback, useEffect, useState } from 'react';
import { Button, Card, Form, Image, Input, InputNumber, Modal, Popover, Segmented, Select, Space, Table, Tabs, Tag, Typography, message } from 'antd';
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table';
import { Link } from 'react-router-dom';
import dayjs from 'dayjs';
import {
  getPartnerShopBasicInfoLookbook,
  getPartnerShopReviewSummary,
  getPartnerShops,
  savePartnerShopBasicInfoLookbook,
  savePartnerShopExtractedShops,
  savePartnerShopReviewSummary,
  updatePartnerShopSortOrder,
  type PartnerShopListItem,
  type PartnerShopStatus,
} from '@/api/partnerShops';
import ExtractedShopSelectStep from './components/ExtractedShopSelectStep';
import type {
  CafeArticleAnalysisShopView,
  CafeArticleImageShopView,
  CafeExtractedShop,
  HeroImageUploadItem,
  HeroImagesJson,
  PartnerShop,
  PartnerShopExtractedShopMap,
  PartnerShopExtractedShopRelationType,
  PartnerShopLookbookPhoto,
  PartnerShopLookbookSection,
  PartnerShopReviewSummary,
  PartnerShopReviewItem,
  PartnerShopReviewPhoto,
  VerifiedReasonsJson,
} from './PartnerCreatePage';
import ReviewPhotoSelectStep from './components/ReviewPhotoSelectStep';
import ReviewItemSelectStep from './components/ReviewItemSelectStep';
import ReviewSummaryStep from './components/ReviewSummaryStep';
import BasicInfoStep from './components/BasicInfoStep';
import LookbookStep from './components/LookbookStep';
import PlannerContentModal from './components/PlannerContentModal';
import { appendHeroImageFiles } from './util';

const { Text } = Typography;

const SHOP_CATEGORY_OPTIONS = [
  // '스튜디오',
  // '드레스',
  // '메이크업',
  '예복',
  '예물',
  '한복',
  '가구',
  '피부관리',
  '답례품',
  '본식스냅',
  // '베뉴',
  // '혼수',
  // '기타',
  // '알수없음',
].map((value) => ({ label: value, value }));

const SHOP_CATEGORY_FILTER_OPTIONS = [
  { value: 'ALL', label: '전체' },
  ...SHOP_CATEGORY_OPTIONS.map((option) => ({
    value: option.value,
    label: option.label,
  })),
];

const STATUS_OPTIONS: { label: string; value: PartnerShopStatus }[] = [
  { label: '활성', value: 'ACTIVE' },
  { label: '초안', value: 'DRAFT' },
  { label: '숨김', value: 'HIDDEN' },
];

const STATUS_TAG: Record<PartnerShopStatus, { color: string; label: string }> = {
  ACTIVE: { color: 'green', label: '활성' },
  DRAFT: { color: 'gold', label: '초안' },
  HIDDEN: { color: 'default', label: '숨김' },
};

const SHOP_CATEGORY_TEXT_COLOR: Record<string, string> = {
  한복: '#722ed1',
  예복: '#1677ff',
  예물: '#d48806',
};

type SearchFormValues = {
  keyword?: string;
  status?: PartnerShopStatus;
};

type EditablePartnerShopExtractedShopMap = PartnerShopExtractedShopMap & {
  memo?: string | null;
};

type EditableLookbookPhoto = PartnerShopLookbookPhoto & {
  imageUrl?: string;
  thumbnailUrl?: string;
  imageUrlW720?: string;
  imageUrlW1080?: string;
};

type EditableLookbookSection = Omit<PartnerShopLookbookSection, 'photos'> & {
  photos: EditableLookbookPhoto[];
};

const EMPTY_HERO_IMAGES_JSON: HeroImagesJson = {
  mobile: {
    main: null,
    items: [null, null, null, null],
  },
  desktop: {
    main: null,
    items: [null, null, null, null],
  },
};

const EMPTY_PARTNER_SHOP: PartnerShop = {
  shopName: '',
  displayName: '',
  shopSlug: '',
  normalizedShopName: '',
  shopCategory: '예복',
  branchName: '',
  areaName: '',
  shortDescription: '',
  brandStory: '',
  address: '',
  lat: null,
  lon: null,
  latitude: null,
  longitude: null,
  openingHours: '',
  parkingInfo: '',
  reservationMethod: '',
  consultationMethod: '',
  phone: '',
  homepageUrl: '',
  naverMapUrl: '',
  googleMapUrl: '',
  badgesJson: [],
  verifiedReasonsJson: {
    introText: '',
    items: [],
  },
  featuresJson: [],
  priceItemsJson: [],
  faqJson: [],
  noticeJson: [],
  heroImagesJson: EMPTY_HERO_IMAGES_JSON,
  promotionBenefitsJson: [],
  isCertified: 1,
  status: 'DRAFT',
  sortOrder: 0,
};

const EMPTY_REVIEW_SUMMARY: PartnerShopReviewSummary = {
  importedLlmDraftId: null,
  totalMentionCount: 0,
  evaluatedReviewCount: 0,
  directReviewCount: 0,
  indirectReviewCount: 0,
  mentionedOnlyCount: 0,
  summaryText: '',
  prosJson: [],
  consJson: [],
  keywordsJson: [],
  sourceCafeArticleIdsJson: [],
  plannerText: '',
  status: 'READY',
  delYn: 0,
};

function toPositiveNumber(value: unknown) {
  const numberValue = Number(value);

  if (Number.isNaN(numberValue) || numberValue < 1) {
    return null;
  }

  return numberValue;
}

function toNumber(value: unknown, fallback: number) {
  const numberValue = Number(value);

  if (Number.isNaN(numberValue)) {
    return fallback;
  }

  return numberValue;
}

function toRelationType(value: string): PartnerShopExtractedShopRelationType {
  
  if (
    value === 'PRIMARY' ||
    value === 'ALIAS' ||
    value === 'BRANCH' ||
    value === 'MERGED'
  ) {
    return value;
  }

  return 'ALIAS';
}

function withPreviewUrl(item: HeroImageUploadItem | null): HeroImageUploadItem | null {
  if (!item) return null;

  const source = item as HeroImageUploadItem & { imageUrl?: string; thumbnailUrl?: string };

  return {
    ...item,
    previewUrl: item.previewUrl || source.imageUrl || source.thumbnailUrl,
  };
}

function normalizeHeroImagesJson(heroImagesJson?: HeroImagesJson | null): HeroImagesJson {
  const mobileItems = heroImagesJson?.mobile?.items || [];
  const desktopItems = heroImagesJson?.desktop?.items || [];

  return {
    mobile: {
      main: withPreviewUrl(heroImagesJson?.mobile?.main || null),
      items: [0, 1, 2, 3].map((index) => withPreviewUrl(mobileItems[index] || null)),
    },
    desktop: {
      main: withPreviewUrl(heroImagesJson?.desktop?.main || null),
      items: [0, 1, 2, 3].map((index) => withPreviewUrl(desktopItems[index] || null)),
    },
  };
}

function stripHeroImagesForSave(heroImagesJson: HeroImagesJson) {
  const stripItem = (item: HeroImageUploadItem | null) => {
    if (!item) return null;

    const source = item as HeroImageUploadItem & {
      imageUrl?: string;
      thumbnailUrl?: string;
      imageUrlW720?: string;
      imageUrlW1080?: string;
    };

    return {
      clientKey: item.clientKey,
      sourceType: item.sourceType,
      originalFileName: item.originalFileName,
      sortOrder: item.sortOrder,
      imageUrl: source.imageUrl || (!item.file ? item.previewUrl : undefined),
      thumbnailUrl: source.thumbnailUrl,
      imageUrlW720: source.imageUrlW720,
      imageUrlW1080: source.imageUrlW1080,
    };
  };

  return {
    mobile: {
      main: stripItem(heroImagesJson.mobile.main),
      items: heroImagesJson.mobile.items.map(stripItem),
    },
    desktop: {
      main: stripItem(heroImagesJson.desktop.main),
      items: heroImagesJson.desktop.items.map(stripItem),
    },
  };
}

function normalizePartnerShop(partnerShop: Partial<PartnerShop>): PartnerShop {
  const lat = partnerShop.lat ?? partnerShop.latitude ?? null;
  const lon = partnerShop.lon ?? partnerShop.longitude ?? null;

  return {
    ...EMPTY_PARTNER_SHOP,
    ...partnerShop,
    lat,
    lon,
    latitude: lat,
    longitude: lon,
    badgesJson: partnerShop.badgesJson || [],
    verifiedReasonsJson:
      partnerShop.verifiedReasonsJson || EMPTY_PARTNER_SHOP.verifiedReasonsJson,
    featuresJson: partnerShop.featuresJson || [],
    priceItemsJson: partnerShop.priceItemsJson || [],
    faqJson: partnerShop.faqJson || [],
    noticeJson: partnerShop.noticeJson || [],
    heroImagesJson: normalizeHeroImagesJson(partnerShop.heroImagesJson),
    promotionBenefitsJson: partnerShop.promotionBenefitsJson || [],
  };
}

function normalizeLookbookSections(
  partnerShopLookbookSections: PartnerShopLookbookSection[]
): EditableLookbookSection[] {
  return (partnerShopLookbookSections || []).map((section, sectionIndex) => ({
    ...section,
    sectionName: section.sectionName || '',
    description: section.description || '',
    isPublished: toNumber(section.isPublished, 1),
    sortOrder: toNumber(section.sortOrder, sectionIndex + 1),
    delYn: toNumber(section.delYn, 0),
    photos: (section.photos || []).map((photo, photoIndex) => {
      const source = photo as EditableLookbookPhoto;

      return {
        ...source,
        lookbookSectionId: source.lookbookSectionId ?? section.id ?? null,
        clientKey:
          source.clientKey ||
          `lookbook_existing_${source.id || section.id || sectionIndex}_${photoIndex}`,
        previewUrl:
          source.previewUrl ||
          source.imageUrlW720 ||
          source.imageUrl ||
          source.thumbnailUrl,
        category: source.category || section.sectionName || '',
        title: source.title || '',
        description: source.description || '',
        isRepresentative: toNumber(source.isRepresentative, 0),
        isPublished: toNumber(source.isPublished, 1),
        sortOrder: toNumber(source.sortOrder, photoIndex + 1),
        delYn: toNumber(source.delYn, 0),
      };
    }),
  }));
}

export default function PartnerShopListPage() {
  const [form] = Form.useForm<SearchFormValues>();
  const [activeShopCategory, setActiveShopCategory] = useState('ALL');
  const [loading, setLoading] = useState(false);
  const [savingExtractedShops, setSavingExtractedShops] = useState(false);
  const [basicInfoLookbookLoading, setBasicInfoLookbookLoading] = useState(false);
  const [reviewSummaryLoading, setReviewSummaryLoading] = useState(false);
  const [savingBasicInfoLookbook, setSavingBasicInfoLookbook] = useState(false);
  const [list, setList] = useState<PartnerShopListItem[]>([]);
  const [editingPartnerShop, setEditingPartnerShop] =
    useState<PartnerShopListItem | null>(null);
  const [editingSortOrderShop, setEditingSortOrderShop] =
    useState<PartnerShopListItem | null>(null);
  const [plannerContentShop, setPlannerContentShop] =
    useState<PartnerShopListItem | null>(null);
  const [editingSortOrder, setEditingSortOrder] = useState<number | null>(null);
  const [savingSortOrder, setSavingSortOrder] = useState(false);
  const [editingBasicInfoLookbookShop, setEditingBasicInfoLookbookShop] =
    useState<PartnerShopListItem | null>(null);
  const [editingBasicInfoPartnerShop, setEditingBasicInfoPartnerShop] =
    useState<PartnerShop>(EMPTY_PARTNER_SHOP);
  const [editingLookbookSections, setEditingLookbookSections] = useState<
    EditableLookbookSection[]
  >([]);
  const [editingReviewSummary, setEditingReviewSummary] =
    useState<PartnerShopReviewSummary>(EMPTY_REVIEW_SUMMARY);
  const [editingExtractedShopMaps, setEditingExtractedShopMaps] = useState<
    EditablePartnerShopExtractedShopMap[]
  >([]);
  const [editingCafeExtractedShops, setEditingCafeExtractedShops] = useState<
    CafeExtractedShop[]
  >([]);
  const [editingReviewItems, setEditingReviewItems] = useState<
    PartnerShopReviewItem[]
  >([]);
  const [editingReviewPhotos, setEditingReviewPhotos] = useState<
    PartnerShopReviewPhoto[]
  >([]);
  const [editingAnalysisShopViews, setEditingAnalysisShopViews] = useState<
    Record<string, CafeArticleAnalysisShopView>
  >({});
  const [editingImageShopViews, setEditingImageShopViews] = useState<
    Record<string, CafeArticleImageShopView>
  >({});
  const [shouldSyncReviewItems, setShouldSyncReviewItems] = useState(false);
  const [shouldSyncReviewPhotos, setShouldSyncReviewPhotos] = useState(false);
  const [shouldSyncReviewSummary, setShouldSyncReviewSummary] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });

  const fetchList = useCallback(async ({
    page = 1,
    rows = 20,
  }: {
    page?: number;
    rows?: number;
  } = {}) => {
    try {
      setLoading(true);

      const values = form.getFieldsValue();
      const result = await getPartnerShops({
        page,
        rows,
        keyword: values.keyword?.trim(),
        shopCategory:
          activeShopCategory === 'ALL' ? undefined : activeShopCategory,
        status: values.status,
      });

      setList(result.list || []);
      setPagination({
        current: result.page || page,
        pageSize: result.rows || rows,
        total: Number(result.total || 0),
      });
    } catch (error) {
      message.error(error instanceof Error ? error.message : '인증업체 목록 조회 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  }, [activeShopCategory, form]);

  useEffect(() => {
    fetchList({ page: 1, rows: 20 });
  }, [fetchList]);

  const handleSearch = () => {
    fetchList({ page: 1, rows: pagination.pageSize });
  };

  const handleReset = () => {
    form.resetFields();
    fetchList({ page: 1, rows: pagination.pageSize });
  };

  const handleChangeShopCategoryTab = (nextShopCategory: string) => {
    setActiveShopCategory(nextShopCategory);
  };

  const handleTableChange = (nextPagination: TablePaginationConfig) => {
    fetchList({
      page: nextPagination.current || 1,
      rows: nextPagination.pageSize || 20,
    });
  };

  const openSortOrderModal = (record: PartnerShopListItem) => {
    setEditingSortOrderShop(record);
    setEditingSortOrder(Number(record.sortOrder || 1));
  };

  const closeSortOrderModal = () => {
    setEditingSortOrderShop(null);
    setEditingSortOrder(null);
  };

  const handleSaveSortOrder = async () => {
    if (!editingSortOrderShop || !editingSortOrder) return;

    try {
      setSavingSortOrder(true);

      await updatePartnerShopSortOrder(
        editingSortOrderShop.id,
        Number(editingSortOrder)
      );

      message.success('노출 순위를 변경했습니다.');
      closeSortOrderModal();
      fetchList({ page: pagination.current, rows: pagination.pageSize });
    } catch (error) {
      message.error(error instanceof Error ? error.message : '노출 순위 변경 중 오류가 발생했습니다.');
    } finally {
      setSavingSortOrder(false);
    }
  };

  const openBasicInfoLookbookModal = async (record: PartnerShopListItem) => {
    setEditingBasicInfoLookbookShop(record);
    setBasicInfoLookbookLoading(true);

    try {
      const result = await getPartnerShopBasicInfoLookbook(record.id);

      setEditingBasicInfoPartnerShop(normalizePartnerShop(result.partnerShop));
      setEditingLookbookSections(
        normalizeLookbookSections(result.partnerShopLookbookSections || [])
      );
    } catch (error) {
      message.error(error instanceof Error ? error.message : '기본정보와 룩북 조회 중 오류가 발생했습니다.');
      setEditingBasicInfoLookbookShop(null);
    } finally {
      setBasicInfoLookbookLoading(false);
    }
  };

  const closeBasicInfoLookbookModal = () => {
    setEditingBasicInfoLookbookShop(null);
    setEditingBasicInfoPartnerShop(EMPTY_PARTNER_SHOP);
    setEditingLookbookSections([]);
  };

  const openExtractedShopModal = async (record: PartnerShopListItem) => {
    setEditingPartnerShop(record);
    setReviewSummaryLoading(true);
    setEditingExtractedShopMaps(
      (record.extractedShops || []).map((item) => ({
        partnerShopId: record.id,
        cafeExtractedShopId: Number(item.cafeExtractedShopId),
        relationType: toRelationType(item.relationType),
        memo: item.memo,
        delYn: 0,
      }))
    );
    setEditingCafeExtractedShops(
      (record.extractedShops || []).map((item) => ({
        id: Number(item.cafeExtractedShopId),
        shopName: item.shopName,
        normalizedShopName: item.normalizedShopName,
        shopCategory: item.shopCategory,
        articleCount: Number(item.articleCount || 0),
        directReviewCount: Number(item.directReviewCount || 0),
        indirectReviewCount: 0,
        imageCount: Number(item.imageCount || 0),
      }))
    );
    setEditingReviewItems([]);
    setEditingReviewPhotos([]);
    setEditingAnalysisShopViews({});
    setEditingImageShopViews({});
    setShouldSyncReviewItems(false);
    setShouldSyncReviewPhotos(false);
    setShouldSyncReviewSummary(false);

    try {
      const [basicInfoLookbook, reviewSummary] = await Promise.all([
        getPartnerShopBasicInfoLookbook(record.id),
        getPartnerShopReviewSummary(record.id).catch(() => EMPTY_REVIEW_SUMMARY),
      ]);

      setEditingBasicInfoPartnerShop(
        normalizePartnerShop(basicInfoLookbook.partnerShop)
      );
      setEditingLookbookSections(
        normalizeLookbookSections(
          basicInfoLookbook.partnerShopLookbookSections || []
        )
      );
      setEditingReviewSummary({
        ...EMPTY_REVIEW_SUMMARY,
        ...reviewSummary,
      });
    } catch (error) {
      message.error(error instanceof Error ? error.message : '후기 요약 정보를 불러오지 못했습니다.');
    } finally {
      setReviewSummaryLoading(false);
    }
  };

  const closeExtractedShopModal = () => {
    setEditingPartnerShop(null);
    setEditingExtractedShopMaps([]);
    setEditingCafeExtractedShops([]);
    setEditingReviewItems([]);
    setEditingReviewPhotos([]);
    setEditingAnalysisShopViews({});
    setEditingImageShopViews({});
    setShouldSyncReviewItems(false);
    setShouldSyncReviewPhotos(false);
    setShouldSyncReviewSummary(false);
    setReviewSummaryLoading(false);
    setEditingReviewSummary(EMPTY_REVIEW_SUMMARY);
    setEditingBasicInfoPartnerShop(EMPTY_PARTNER_SHOP);
    setEditingLookbookSections([]);
  };

  const handleChangeExtractedShopMaps = (
    nextMaps: PartnerShopExtractedShopMap[]
  ) => {
    setEditingExtractedShopMaps(nextMaps);
  };

  const handleChangeReviewItems = (nextReviewItems: PartnerShopReviewItem[]) => {
    setShouldSyncReviewItems(true);
    setEditingReviewItems(nextReviewItems);
  };

  const handleChangeReviewPhotos = (nextReviewPhotos: PartnerShopReviewPhoto[]) => {
    setShouldSyncReviewPhotos(true);
    setEditingReviewPhotos(nextReviewPhotos);
  };

  const handleChangeReviewSummary = (
    nextReviewSummary: PartnerShopReviewSummary
  ) => {
    setShouldSyncReviewSummary(true);
    setEditingReviewSummary(nextReviewSummary);
  };

  const handleChangeVerifiedReasonsJson = (
    nextVerifiedReasonsJson: VerifiedReasonsJson
  ) => {
    setShouldSyncReviewSummary(true);
    setEditingBasicInfoPartnerShop((prev) => ({
      ...prev,
      verifiedReasonsJson: nextVerifiedReasonsJson,
    }));
  };

  const buildReviewItemsPayload = () => {
    return editingReviewItems
      .filter((item) => Number(item.isPublished) === 1)
      .map((item, index) => ({
        cafeArticleAnalysisShopId: toPositiveNumber(
          item.cafeArticleAnalysisShopId
        ),
        isPublished: toNumber(item.isPublished, 1),
        sortOrder: toNumber(item.sortOrder, index + 1),
        delYn: toNumber(item.delYn, 0),
      }))
      .filter((item) => item.cafeArticleAnalysisShopId)
      .map((item) => ({
        ...item,
        cafeArticleAnalysisShopId: Number(item.cafeArticleAnalysisShopId),
      }));
  };

  const buildReviewPhotosPayload = () => {
    return editingReviewPhotos
      .filter((photo) => Number(photo.isPublished) === 1)
      .map((photo, index) => ({
        cafeArticleImageId: toPositiveNumber(photo.cafeArticleImageId),
        cafeArticleImageShopId: toPositiveNumber(photo.cafeArticleImageShopId),
        shopImageCategoryId: toPositiveNumber(photo.shopImageCategoryId),
        caption: photo.caption || '',
        isRepresentative: Number(photo.sortOrder || index + 1) === 1 ? 1 : 0,
        isPublished: toNumber(photo.isPublished, 1),
        sortOrder: toNumber(photo.sortOrder, index + 1),
        delYn: toNumber(photo.delYn, 0),
      }))
      .filter((photo) => photo.cafeArticleImageId && photo.cafeArticleImageShopId)
      .map((photo) => ({
        ...photo,
        cafeArticleImageId: Number(photo.cafeArticleImageId),
        cafeArticleImageShopId: Number(photo.cafeArticleImageShopId),
      }));
  };

  const buildLookbookSectionsPayload = () => {
    return editingLookbookSections.map((section, sectionIndex) => ({
      id: section.id,
      sectionName: section.sectionName || '',
      description: section.description || '',
      isPublished: toNumber(section.isPublished, 1),
      sortOrder: toNumber(section.sortOrder, sectionIndex + 1),
      delYn: toNumber(section.delYn, 0),
      photos: (section.photos || []).map((photo, photoIndex) => ({
        id: photo.id,
        lookbookSectionId: photo.lookbookSectionId ?? section.id ?? null,
        clientKey: photo.clientKey,
        category: photo.category || section.sectionName || '기타',
        title: photo.title || '',
        description: photo.description || '',
        imageUrl: photo.imageUrl,
        thumbnailUrl: photo.thumbnailUrl,
        imageUrlW720: photo.imageUrlW720,
        imageUrlW1080: photo.imageUrlW1080,
        isRepresentative: toNumber(photo.isRepresentative, 0),
        isPublished: toNumber(photo.isPublished, 1),
        sortOrder: toNumber(photo.sortOrder, photoIndex + 1),
        delYn: toNumber(photo.delYn, 0),
      })),
    }));
  };

  const handleSaveBasicInfoLookbook = async () => {
    if (!editingBasicInfoLookbookShop) return;

    try {
      setSavingBasicInfoLookbook(true);

      const formData = new FormData();
      const payload = {
        partnerShop: {
          ...editingBasicInfoPartnerShop,
          shopName: editingBasicInfoLookbookShop.displayName,
          heroImagesJson: stripHeroImagesForSave(
            editingBasicInfoPartnerShop.heroImagesJson
          ),
        },
        partnerShopLookbookSections: buildLookbookSectionsPayload(),
      };

      formData.append('payload', JSON.stringify(payload));
      appendHeroImageFiles(formData, editingBasicInfoPartnerShop.heroImagesJson);

      editingLookbookSections.forEach((section) => {
        section.photos.forEach((photo) => {
          if (photo.file && photo.clientKey) {
            formData.append(
              `partnerShopLookbookPhotos.${photo.clientKey}`,
              photo.file
            );
          }
        });
      });

      await savePartnerShopBasicInfoLookbook(
        editingBasicInfoLookbookShop.id,
        formData
      );

      message.success('기본정보와 룩북을 저장했습니다.');
      closeBasicInfoLookbookModal();
      fetchList({ page: pagination.current, rows: pagination.pageSize });
    } catch (error) {
      message.error(error instanceof Error ? error.message : '기본정보와 룩북 저장 중 오류가 발생했습니다.');
    } finally {
      setSavingBasicInfoLookbook(false);
    }
  };

  const handleSaveExtractedShops = async () => {
    if (!editingPartnerShop) return;

    try {
      setSavingExtractedShops(true);

      await savePartnerShopExtractedShops(editingPartnerShop.id, {
        partnerShopExtractedShopMaps: editingExtractedShopMaps
          .map((item) => ({
            cafeExtractedShopId: Number(item.cafeExtractedShopId),
            relationType: item.relationType || 'ALIAS',
            memo: item.memo ?? null,
            delYn: Number(item.delYn || 0),
          }))
          .filter((item) => item.cafeExtractedShopId),
        ...(shouldSyncReviewItems
          ? { partnerShopReviewItems: buildReviewItemsPayload() }
          : {}),
        ...(shouldSyncReviewPhotos
          ? { partnerShopReviewPhotos: buildReviewPhotosPayload() }
          : {}),
      });

      if (shouldSyncReviewSummary) {
        await Promise.all([
          savePartnerShopReviewSummary(editingPartnerShop.id, editingReviewSummary),
          savePartnerShopBasicInfoLookbook(
            editingPartnerShop.id,
            (() => {
              const formData = new FormData();

              formData.append(
                'payload',
                JSON.stringify({
                  partnerShop: {
                    ...editingBasicInfoPartnerShop,
                    heroImagesJson: stripHeroImagesForSave(
                      editingBasicInfoPartnerShop.heroImagesJson
                    ),
                  },
                  partnerShopLookbookSections: buildLookbookSectionsPayload(),
                })
              );

              appendHeroImageFiles(
                formData,
                editingBasicInfoPartnerShop.heroImagesJson
              );

              editingLookbookSections.forEach((section) => {
                section.photos.forEach((photo) => {
                  if (photo.file && photo.clientKey) {
                    formData.append(
                      `partnerShopLookbookPhotos.${photo.clientKey}`,
                      photo.file
                    );
                  }
                });
              });

              return formData;
            })()
          ),
        ]);
      }

      message.success('AI 추출 업체 연결을 저장했습니다.');
      closeExtractedShopModal();
      fetchList({ page: pagination.current, rows: pagination.pageSize });
    } catch (error) {
      message.error(error instanceof Error ? error.message : 'AI 추출 업체 연결 저장 중 오류가 발생했습니다.');
    } finally {
      setSavingExtractedShops(false);
    }
  };

  const columns: ColumnsType<PartnerShopListItem> = [
    {
      title: '업체',
      dataIndex: 'shopName',
      width: 220,
      render: (_, record) => (
        <Space size={8} align="center">
          {record.heroImageUrl ? (
            <Image
              src={record.heroImageUrl}
              width={48}
              height={48}
              style={{ objectFit: 'cover', borderRadius: 6, background: '#f5f5f5' }}
              preview
            />
          ) : (
            <div
              style={{
                width: 48,
                height: 48,
                borderRadius: 6,
                background: '#f5f5f5',
                border: '1px solid #f0f0f0',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#999',
                fontSize: 11,
                flex: '0 0 auto',
              }}
            >
              사진없음
            </div>
          )}
          <Space direction="vertical" size={0}>
            <Space size={4} wrap={false}>
              <Text strong ellipsis style={{ maxWidth: 128, lineHeight: '18px' }}>
                {record.displayName || record.shopName}
              </Text>
              {Number(record.isCertified) === 1 && (
                <Tag color="blue" style={{ marginInlineEnd: 0, lineHeight: '18px' }}>
                  인증
                </Tag>
              )}
            </Space>
            <Text type="secondary" ellipsis style={{ fontSize: 12, maxWidth: 144, lineHeight: '16px' }}>
              {record.shopSlug || '-'}
            </Text>
          </Space>
        </Space>
      ),
    },
    {
      title: '분류/지역',
      width: 84,
      render: (_, record) => (
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            width: 76,
            gap: 6,
            lineHeight: '18px',
          }}
        >
          <Text
            style={{
              color: SHOP_CATEGORY_TEXT_COLOR[record.shopCategory || ''] || '#555',
              fontSize: 12,
              fontWeight: 600,
              lineHeight: '18px',
              width: 26,
            }}
          >
            {record.shopCategory || '-'}
          </Text>
          <Text
            ellipsis
            style={{ color: '#111', width: 44, fontSize: 12, lineHeight: '18px' }}
          >
            {[record.areaName, record.branchName].filter(Boolean).join(' / ') || '-'}
          </Text>
        </span>
      ),
    },
    {
      title: '상태',
      dataIndex: 'status',
      width: 56,
      align: 'center',
      render: (status: PartnerShopStatus) => {
        const tag = STATUS_TAG[status] || { color: 'default', label: status };
        return (
          <Text
            style={{
              color: tag.color === 'green' ? '#389e0d' : undefined,
              fontSize: 12,
              lineHeight: '18px',
            }}
          >
            {tag.label}
          </Text>
        );
      },
    },
    {
      title: '추출업체',
      dataIndex: 'extractedShopCount',
      width: 82,
      align: 'center',
      render: (_, record) => {
        const extractedShops = record.extractedShops || [];

        return (
          <Popover
            trigger={['hover', 'click']}
            placement="right"
            content={
              extractedShops.length === 0 ? (
                <Text type="secondary">연결된 업체가 없습니다.</Text>
              ) : (
                <Space direction="vertical" size={4}>
                  {extractedShops.map((item) => (
                    <Space key={item.mapId} size={6}>
                      {/* <Tag
                        color={item.relationType === 'PRIMARY' ? 'processing' : 'default'}
                        style={{ marginInlineEnd: 0 }}
                      >
                        {item.relationType}
                      </Tag> */}
                      <Text>{item.shopName}</Text>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        글 {item.articleCount || 0} / 사진 {item.imageCount || 0}
                      </Text>
                    </Space>
                  ))}
                </Space>
              )
            }
          >
            <Typography.Link style={{ fontSize: 12, lineHeight: '18px' }}>
              {record.extractedShopCount || 0}개 연결
            </Typography.Link>
          </Popover>
        );
      },
    },
    
    {
      title: '룩북',
      width: 112,
      render: (_, record) => (
        <Text style={{ fontSize: 12, lineHeight: '18px' }}>
          섹션 {record.lookbookSectionCount || 0} / 사진 {record.lookbookPhotoCount || 0}
        </Text>
      ),
    },
    {
      title: '후기 연결',
      width: 132,
      render: (_, record) => (
        <Text style={{ fontSize: 12, lineHeight: '18px' }}>
          후기 {record.publishedReviewItemCount || 0}/{record.reviewItemCount || 0}
          {' · '}
          사진 {record.publishedReviewPhotoCount || 0}/{record.reviewPhotoCount || 0}
        </Text>
      ),
    },
    // {
    //   title: '요약',
    //   width: 260,
    //   render: (_, record) => (
    //     <Space direction="vertical" size={4}>
    //       {record.hasReviewSummary ? (
    //         <Tag color={record.reviewSummaryStatus === 'READY' ? 'green' : 'gold'}>
    //           {record.reviewSummaryStatus || '작성됨'}
    //         </Tag>
    //       ) : (
    //         <Tag>없음</Tag>
    //       )}
    //       <Text ellipsis={{ tooltip: record.reviewSummaryText || undefined }}>
    //         {record.reviewSummaryText || record.shortDescription || '-'}
    //       </Text>
    //     </Space>
    //   ),
    // },
    {
      title: '정렬',
      dataIndex: 'sortOrder',
      width: 52,
      align: 'center',
      render: (value, record) => (
        <Button
          title='순서 변경'
          size="small"
          style={{ minWidth: 32, height: 22, padding: '0 8px', fontWeight: 600 }}
          onClick={() => openSortOrderModal(record)}
        >
          {value}
        </Button>
      ),
    },
    {
      title: '수정일',
      dataIndex: 'mdfTime',
      width: 86,
      render: (value) => {
        if (!value) return '-';
        return (
          <Text style={{ fontSize: 12 }}>
            {dayjs(value).isValid() ? dayjs(value).format('MM-DD HH:mm') : value}
          </Text>
        );
      },
    },
    // {
    //   title: '생성일',
    //   dataIndex: 'creationTime',
    //   width: 160,
    //   render: formatDate,
    // },
    {
      title: '관리',
      width: 196,
      render: (_, record) => (
        <Space size={7} wrap={false}>
          <Typography.Link
            style={{ fontSize: 12, lineHeight: '18px' }}
            onClick={() => openBasicInfoLookbookModal(record)}
          >
            기본/룩북
          </Typography.Link>
          |
          <Typography.Link
            style={{ fontSize: 12, lineHeight: '18px' }}
            onClick={() => openExtractedShopModal(record)}
          >
            카페 후기 연동
          </Typography.Link>
          |
          <Typography.Link
            style={{ fontSize: 12, lineHeight: '18px' }}
            onClick={() => setPlannerContentShop(record)}
          >
            플래너 콘텐츠
          </Typography.Link>
        </Space>
      ),
    },
  ];

  return (
    <Card
      title="인증업체 관리"
      extra={
        <Button type="primary">
          <Link to="/partner-shops/new">인증업체 등록</Link>
        </Button>
      }
    >
      <Form form={form} layout="inline" style={{ marginBottom: 16 }}>
        <Form.Item>
          <Segmented
            size="medium"
            value={activeShopCategory}
            options={SHOP_CATEGORY_FILTER_OPTIONS}
            onChange={(value) => handleChangeShopCategoryTab(String(value))}
          />
        </Form.Item>

        <Form.Item name="keyword">
          <Input
            allowClear
            placeholder="업체명 / 표시명 / 슬러그 검색"
            style={{ width: 260 }}
            onPressEnter={handleSearch}
          />
        </Form.Item>

        <Form.Item name="status">
          <Select
            allowClear
            placeholder="상태"
            style={{ width: 120 }}
            options={STATUS_OPTIONS}
          />
        </Form.Item>

        <Form.Item>
          <Space>
            <Button type="primary" onClick={handleSearch}>
              검색
            </Button>
            <Button onClick={handleReset}>초기화</Button>
          </Space>
        </Form.Item>
      </Form>

      <Table<PartnerShopListItem>
        rowKey="id"
        className="partner-shop-compact-table"
        size="small"
        loading={loading}
        columns={columns}
        dataSource={list}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          showSizeChanger: true,
        }}
        onChange={handleTableChange}
      />

      <style>
        {`
          .partner-shop-compact-table .ant-table-thead > tr > th {
            padding: 6px 8px;
            font-size: 12px;
            white-space: nowrap;
          }

          .partner-shop-compact-table .ant-table-tbody > tr > td {
            padding: 4px 8px;
            vertical-align: middle;
          }

          .partner-shop-compact-table .ant-tag {
            font-size: 11px;
            line-height: 18px;
            padding-inline: 6px;
          }
        `}
      </style>

      <Modal
        title="노출 순위 변경"
        open={!!editingSortOrderShop}
        onCancel={closeSortOrderModal}
        onOk={handleSaveSortOrder}
        okText="변경"
        cancelText="취소"
        confirmLoading={savingSortOrder}
        destroyOnHidden
      >
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
          <div>
            <Text strong>
              {editingSortOrderShop?.displayName || editingSortOrderShop?.shopName}
            </Text>
            <Text type="secondary" style={{ marginLeft: 8 }}>
              {editingSortOrderShop?.shopCategory || '분류없음'} 안에서 재정렬됩니다.
            </Text>
          </div>

          <InputNumber
            min={1}
            value={editingSortOrder}
            style={{ width: '100%' }}
            addonBefore="노출 순위"
            onChange={(value) => setEditingSortOrder(value ? Number(value) : null)}
          />
        </Space>
      </Modal>

      <PlannerContentModal
        partnerShop={plannerContentShop}
        onClose={() => setPlannerContentShop(null)}
      />

      <Modal
        title={
          editingBasicInfoLookbookShop
            ? `${editingBasicInfoLookbookShop.displayName || editingBasicInfoLookbookShop.shopName} - 기본정보/룩북`
            : '기본정보/룩북'
        }
        open={!!editingBasicInfoLookbookShop}
        width={1280}
        onCancel={closeBasicInfoLookbookModal}
        onOk={handleSaveBasicInfoLookbook}
        okText="저장"
        cancelText="취소"
        confirmLoading={savingBasicInfoLookbook}
        destroyOnHidden
      >
        <Tabs
          items={[
            {
              key: 'basic-info',
              label: '기본정보',
              children: basicInfoLookbookLoading ? (
                <Card loading />
              ) : (
                <BasicInfoStep
                  partnerShop={editingBasicInfoPartnerShop}
                  onChangePartnerShop={setEditingBasicInfoPartnerShop}
                />
              ),
            },
            {
              key: 'lookbook',
              label: `룩북 ${editingLookbookSections.length.toLocaleString()}`,
              children: basicInfoLookbookLoading ? (
                <Card loading />
              ) : (
                <LookbookStep
                  partnerShopLookbookSections={editingLookbookSections}
                  onChangePartnerShopLookbookSections={(nextSections) =>
                    setEditingLookbookSections(nextSections as EditableLookbookSection[])
                  }
                />
              ),
            },
          ]}
        />
      </Modal>

      <Modal
        title={
          editingPartnerShop
            ? `${editingPartnerShop.displayName || editingPartnerShop.shopName} - AI 추출 업체 연결`
            : 'AI 추출 업체 연결'
        }
        open={!!editingPartnerShop}
        width={1280}
        onCancel={closeExtractedShopModal}
        onOk={handleSaveExtractedShops}
        okText="저장"
        cancelText="취소"
        confirmLoading={savingExtractedShops}
        destroyOnHidden
      >
        {editingPartnerShop && (
          <Tabs
            items={[
              {
                key: 'extracted-shops',
                label: 'AI 추출 업체',
                children: (
                  <ExtractedShopSelectStep
                    partnerShopExtractedShopMaps={editingExtractedShopMaps}
                    onChangePartnerShopExtractedShopMaps={
                      handleChangeExtractedShopMaps
                    }
                    cafeExtractedShops={editingCafeExtractedShops}
                    onChangeCafeExtractedShops={setEditingCafeExtractedShops}
                  />
                ),
              },
              {
                key: 'review-photos',
                label: `후기 사진 ${editingReviewPhotos.length.toLocaleString()}`,
                children: (
                  <ReviewPhotoSelectStep
                    partnerShopId={editingPartnerShop.id}
                    partnerShopExtractedShopMaps={editingExtractedShopMaps}
                    partnerShopReviewPhotos={editingReviewPhotos}
                    onChangePartnerShopReviewPhotos={handleChangeReviewPhotos}
                    cafeArticleImageShopViews={editingImageShopViews}
                    onChangeCafeArticleImageShopViews={setEditingImageShopViews}
                  />
                ),
              },
              {
                key: 'review-items',
                label: `후기 ${editingReviewItems.length.toLocaleString()}`,
                children: (
                  <ReviewItemSelectStep
                    partnerShopId={editingPartnerShop.id}
                    partnerShopExtractedShopMaps={editingExtractedShopMaps}
                    partnerShopReviewItems={editingReviewItems}
                    onChangePartnerShopReviewItems={handleChangeReviewItems}
                    cafeArticleAnalysisShopViews={editingAnalysisShopViews}
                    onChangeCafeArticleAnalysisShopViews={
                      setEditingAnalysisShopViews
                    }
                    onGeneratePartnerShopReviewSummary={() => {
                      message.info('목록 모달에서는 후기 연결 저장만 지원합니다.');
                    }}
                    generatingPartnerShopReviewSummary={false}
                  />
                ),
              },
              {
                key: 'review-summary',
                label: '후기 요약',
                children: reviewSummaryLoading ? (
                  <Card loading />
                ) : (
                  <ReviewSummaryStep
                    shopName={
                      editingBasicInfoPartnerShop.displayName ||
                      editingBasicInfoPartnerShop.shopName ||
                      editingPartnerShop.shopName
                    }
                    sourceCafeExtractedShopIds={editingExtractedShopMaps
                      .map((item) => Number(item.cafeExtractedShopId))
                      .filter((id) => !Number.isNaN(id) && id > 0)}
                    partnerShopReviewSummary={editingReviewSummary}
                    onChangePartnerShopReviewSummary={handleChangeReviewSummary}
                    verifiedReasonsJson={
                      editingBasicInfoPartnerShop.verifiedReasonsJson
                    }
                    onChangeVerifiedReasonsJson={
                      handleChangeVerifiedReasonsJson
                    }
                    partnerShopId={editingPartnerShop.id}
                  />
                ),
              },
            ]}
          />
        )}
      </Modal>
    </Card>
  );
}
